import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { Controle } from "../../models/controle.model";
import { ControlePod } from "../../models/controle-pod.model";
import { Checklist } from "../../../list/models/checklist.model";
import { ElementTypeControle } from "../../enums/element-type-controle";
import {
  ControleHistory,
  ControleHistoryFilters,
} from "../../models/controle-history.model";

@Injectable({ providedIn: "root" })
export class ControleApiService implements EntityApiService<Controle> {
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<Controle> {
    throw new Error("Method not implemented.");
  }

  answer(entity: Controle): Observable<Controle> {
    return this.httpClient.post<Controle>(
      `${environment.api()}/controle/answer`,
      entity,
    );
  }

  /**
   * Enregistre un contrôle **depuis le cockpit d'un essai** — la route de la formulation.
   *
   * ⚠️ Elle existe pour une raison précise : elle fournit à la comparaison de **marquage** le numéro de
   * lot (celui du projet de recette) et la date limite calculée, que la route générique n'a pas. Même
   * patron que `answerForChecklist`, l'échappatoire déjà en place pour les contrôles sans poste.
   */
  /**
   * ⚠️ **`operationEssaiId` voyage en paramètre de REQUÊTE, jamais dans le corps** : le corps est
   * l'entité `Controle` telle que le serveur la relit et la réenregistre. Y glisser un champ d'écran
   * l'exposerait à être persisté par un autre chemin — et le rattachement est un **fait de saisie**,
   * posé par cette route seule.
   *
   * ⚠️ **Absent, le paramètre n'efface RIEN** côté serveur : re-répondre un relevé déjà rattaché depuis
   * un écran qui ne connaît pas l'opération conserverait son rattachement. C'est ce qui rend ce
   * paramètre sûr à omettre — et donc inutile à faire descendre partout.
   */
  answerForEssai(
    entity: Controle,
    essaiId: string,
    operationEssaiId?: string | null,
  ): Observable<Controle> {
    return this.httpClient.post<Controle>(
      `${environment.api()}/recette/essais/${essaiId}/controles/answer`,
      entity,
      operationEssaiId ? { params: { operationEssaiId } } : {},
    );
  }

  answerForChecklist(
    entity: Controle,
    checklistId: string,
  ): Observable<{ controle: Controle; checklist: Checklist }> {
    return this.httpClient.post<{ controle: Controle; checklist: Checklist }>(
      `${environment.api()}/checklist/answer/controle/${checklistId}`,
      entity,
    );
  }

  create(entity: Controle): Observable<Controle> {
    return this.httpClient.post<Controle>(
      `${environment.api()}/controle`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    return this.httpClient.delete<string>(
      `${environment.api()}/controle/${id}`,
    );
  }

  update(entity: Controle): Observable<Controle> {
    return this.httpClient.post<Controle>(
      `${environment.api()}/controle`,
      entity,
    );
  }

  loadAll(): Observable<Controle[]> {
    throw new Error("Method not implemented.");
  }

  loadForPodId(podId: string): Observable<Controle[]> {
    return this.httpClient.get<Controle[]>(
      `${environment.api()}/controle/podId/${podId}`,
    );
  }

  loadForPodIds(podIds: string[], posteIds: string[] = []): Observable<Controle[]> {
    return this.httpClient.post<Controle[]>(
      `${environment.api()}/controle/podIds/`,
      { podIds, posteIds },
    );
  }

  duplicate(entity: Controle): Observable<Controle> {
    return this.httpClient.post<Controle>(
      `${environment.api()}/controle/duplicate`,
      entity,
    );
  }

  saveAll(payload: { controles: Controle[] }) {
    return this.httpClient.post<Controle[]>(
      `${environment.api()}/controle/all`,
      payload,
    );
  }

  createPod() {
    return this.httpClient.post<ControlePod>(
      `${environment.api()}/controle_pod/create`,
      null,
    );
  }

  /** Historique des réponses de production d'un contrôle définition. */
  loadHistory(
    controleId: string,
    filters?: ControleHistoryFilters,
  ): Observable<ControleHistory> {
    return this.httpClient.get<ControleHistory>(
      `${environment.api()}/controle/history/${controleId}`,
      { params: this.historyParams(filters) },
    );
  }

  /** Même DTO, plafond serveur relevé à 5000 entrées — pour l'export Excel. */
  loadHistoryExport(
    controleId: string,
    filters?: ControleHistoryFilters,
  ): Observable<ControleHistory> {
    return this.httpClient.get<ControleHistory>(
      `${environment.api()}/controle/history/${controleId}/export`,
      { params: this.historyParams(filters) },
    );
  }

  private historyParams(filters?: ControleHistoryFilters): {
    [k: string]: string;
  } {
    const params: { [k: string]: string } = {};
    for (const [k, v] of Object.entries(filters ?? {})) {
      if (v !== undefined && v !== null && v !== "") params[k] = String(v);
    }
    return params;
  }

  loadControlesGeneraux(elemType?: ElementTypeControle) {
    return this.httpClient.get<Controle[]>(
      `${environment.api()}/controle/general/${elemType}`,
    );
  }
}
